/**
 * Luvè Live Tree.
 *
 * The second slide of the product media gallery (a photo leads): the selected tree drawn to scale in a room, beside a 165cm person
 * and a 2.04 m doorway, or on a 75cm dining table beside a wine glass for the tabletop sizes. It follows Horizon's
 * own variant picker. Choosing a colour or a height animates the drawing in place, and the element is carried across
 * the gallery re-render Horizon does after every selection, so an animation never restarts or jumps.
 *
 * Markup, palette and the first frame: snippets/luve-live-tree.liquid (server-rendered, complete without JavaScript).
 * Tree shapes for the first frame: snippets/luve-live-tree-shape.liquid, printed from the geometry block below by
 * tools/live-tree-shapes.mjs. Run it after changing the geometry.
 */
import { StandardEvents } from '@shopify/events';
import { SlideshowSelectEvent } from '@theme/events';
import { getScrollContainer } from '@theme/scroll-container';

// <geometry>
// Shared with tools/live-tree-shapes.mjs. Keep this block free of imports and DOM access.
const r4 = (n) => Math.round(n * 10000) / 10000;
/** A point as path data. @param {number[]} xy */
const point = ([x, y]) => `${r4(x)} ${r4(y)}`;
const mix = (a, b, t) => a + (b - a) * t;

/*
 * The trees are drawn in units of tree height: y 0 is the base and -1 the tip. Both are three parts, as the real
 * trees are (Olivia, 18 Oct: "my trees are three tiers"; the supplier's trees assemble "in 3 parts").
 *
 * Measured on 18 Oct from the supplier's own photos of the real trees: the same factory and colour chart as Luvè's
 * (Olivia: "identical"), photographed by Neue Blvd, which sells them. Silhouettes were traced from the clearest
 * shots (a Sage and a red floor tree against white walls, a dark green rounded tree against a plain wall, the tabletop
 * trees on a kitchen table). The tabletop and the floor sizes are different shapes, so Forma is drawn from its height.
 */

/** Forma, 30 to 60cm: a broad cone of three tiers with long fins, the base as wide as 58% of the height. */
const FORMA_TABLE = {
  depth: 0.26,
  tiers: [
    { top: -1.0, bot: -0.66, ht: 0.0, hb: 0.167 },
    { top: -0.74, bot: -0.36, ht: 0.1, hb: 0.235 },
    { top: -0.44, bot: 0.0, ht: 0.16, hb: 0.29 },
  ],
};

/**
 * Forma, 150cm and up: a slim spire about a third as wide as it is tall. A pointed top cone over two near-upright
 * skirts; each tier starts a little narrower than the hem above, so every hem steps out over the next. Hems at 31% and
 * 64% of the height, hem half-widths 0.095, 0.137 and 0.158.
 */
const FORMA_FLOOR = {
  depth: 0.2,
  tiers: [
    { top: -1.0, bot: -0.685, ht: 0.0, hb: 0.095 },
    { top: -0.71, bot: -0.355, ht: 0.066, hb: 0.137 },
    { top: -0.375, bot: 0.0, ht: 0.106, hb: 0.158 },
  ],
};

/**
 * Élan: an onion-shaped tip over a cone of disc rings, then a bell of bigger rings, the two lowest a broad band that
 * tucks in only at the floor (widest, 0.248 of the height, from about 0.8 to 0.9 of the way down). Each part lists its rings top to
 * bottom as [top y, bottom y, widest half-width, half-width where it tucks in, where in the ring it is widest].
 */
const ELAN_PARTS = [
  {
    start: [0.004, -1.0],
    rings: [
      [-1.0, -0.835, 0.072, 0.05, 0.66],
      [-0.835, -0.75, 0.085, 0.062],
      [-0.75, -0.67, 0.095, 0.075],
      [-0.67, -0.61, 0.106, 0.088],
      [-0.61, -0.55, 0.122, 0.105],
      [-0.55, -0.48, 0.14, 0.122],
      [-0.48, -0.42, 0.15, 0.14],
    ],
  },
  {
    start: [0.14, -0.44],
    rings: [
      [-0.44, -0.35, 0.193, 0.184],
      [-0.35, -0.27, 0.205, 0.198],
    ],
  },
  {
    start: [0.19, -0.29],
    rings: [
      [-0.29, -0.16, 0.243, 0.232, 0.72],
      [-0.16, 0.0, 0.248, 0.2, 0.62],
    ],
  },
];

/**
 * How far a Forma of this height sits between the tabletop shape (0) and the floor shape (1). The supplier makes the
 * tabletop sizes up to 60cm and the floor sizes from 150cm; a 90cm falls between them, on a log scale.
 *
 * @param {number} heightCm
 */
function formaBlend(heightCm) {
  const t = Math.log(heightCm / 60) / Math.log(150 / 60);
  return Math.min(1, Math.max(0, t));
}

/** The three tiers of a Forma of this height, top first, each with the depth of its fringe. @param {number} heightCm */
function formaTiers(heightCm) {
  const t = formaBlend(heightCm);
  const depth = mix(FORMA_TABLE.depth, FORMA_FLOOR.depth, t);
  return FORMA_TABLE.tiers.map((a, i) => {
    const b = FORMA_FLOOR.tiers[i];
    return { top: mix(a.top, b.top, t), bot: mix(a.bot, b.bot, t), ht: mix(a.ht, b.ht, t), hb: mix(a.hb, b.hb, t), depth };
  });
}

/**
 * The widest half-width of a tree, in tree heights.
 *
 * @param {string} model - 'forma' or 'elan'
 * @param {number} heightCm
 */
function halfFor(model, heightCm) {
  if (model === 'elan') return 0.248;
  return mix(FORMA_TABLE.tiers[2].hb, FORMA_FLOOR.tiers[2].hb, formaBlend(heightCm));
}

/** Changes whenever the drawn shape does: a Forma changes shape with its height. */
function shapeKey(model, heightCm) {
  return model === 'elan' ? 'elan' : `forma-${formaBlend(heightCm).toFixed(3)}`;
}

/** Fins across the front of a tier: broad blades on a tabletop Forma, finer on a floor Forma, finest on Élan. */
function finsFor(model, heightCm) {
  return model === 'elan' ? 30 : Math.round(mix(12, 20, formaBlend(heightCm)));
}

const SHAPES = {
  forma: {
    outline(t, count) {
      // Straight down the right side to the outer fin point, along the fringe from right to left, and back up
      const hem = formaHem(t, count);
      return `M${r4(-t.ht)} ${t.top}L${r4(t.ht)} ${t.top}L${hem.slice().reverse().map(point).join('L')}Z`;
    },
    // The ridge of every fin, from the top of the tier down to its point: the folds that catch the light
    pleats(t, count) {
      const hem = formaHem(t, count);
      let d = '';
      for (let k = 1; k < count; k++) d += `M${r4(formaAt(k, count) * t.ht)} ${t.top}L${point(hem[2 * k])}`;
      return d;
    },
    rim(t, count) {
      return `M${formaHem(t, count).map(point).join('L')}`;
    },
    // Forma has no rings: the shadow under each hem is the shade at the top of the tier below
    shadows() {
      return '';
    },
    // Each fin is a fold of paper, lit on the side that faces the light and shadowed on the other: the shadowed face
    // runs from a fin's ridge (down to its point) to the fold before the next fin (down to the notch)
    facets(t, count) {
      const hem = formaHem(t, count);
      let d = '';
      for (let k = 0; k < count; k++) {
        d +=
          `M${r4(formaAt(k, count) * t.ht)} ${t.top}L${r4(formaAt(k + 0.5, count) * t.ht)} ${t.top}` +
          `L${point(hem[2 * k + 1])}L${point(hem[2 * k])}Z`;
      }
      return d;
    },
  },
  elan: {
    outline(t) {
      const { start, segments, end } = elanSide(t);
      const sag = elanSag(end[0]);
      const right = segments.map((s) => `C${s.map(r4).join(' ')}`).join('');
      // The left side is the right side mirrored and walked back up
      let left = '';
      for (let i = segments.length - 1; i >= 0; i--) {
        const [c1x, c1y, c2x, c2y] = segments[i];
        const [x, y] = i > 0 ? segments[i - 1].slice(4) : start;
        left += `C${r4(-c2x)} ${r4(c2y)} ${r4(-c1x)} ${r4(c1y)} ${r4(-x)} ${r4(y)}`;
      }
      return (
        `M${r4(-start[0])} ${r4(start[1])}L${r4(start[0])} ${r4(start[1])}${right}` +
        `Q0 ${r4(end[1] + 2 * sag)} ${r4(-end[0])} ${r4(end[1])}${left}Z`
      );
    },
    // Each pleat is the part's own side narrowed towards the middle, so it follows every ring and can never leave
    // the paper, then drops to the hem
    pleats(t, count) {
      const { start, segments, end } = elanSide(t);
      const sag = elanSag(end[0]);
      let d = '';
      for (let k = 1; k < count; k++) {
        const f = -Math.cos((Math.PI * k) / count);
        d += `M${r4(f * start[0])} ${r4(start[1])}`;
        for (const [c1x, c1y, c2x, c2y, x, y] of segments) {
          d += `C${r4(f * c1x)} ${r4(c1y)} ${r4(f * c2x)} ${r4(c2y)} ${r4(f * x)} ${r4(y)}`;
        }
        d += `L${r4(f * end[0])} ${r4(end[1] + sag * (1 - f * f))}`;
      }
      return d;
    },
    // Every other sector between two pleats, following the rings down to the hem: the shadowed sides of the fins
    facets(t, count) {
      const { start, segments, end } = elanSide(t);
      const sag = elanSag(end[0]);
      const hemY = (f) => end[1] + sag * (1 - f * f);
      let d = '';
      for (let k = 0; k < count; k += 2) {
        const f0 = -Math.cos((Math.PI * k) / count);
        const f1 = -Math.cos((Math.PI * (k + 1)) / count);
        // Down the first pleat
        d += `M${r4(f0 * start[0])} ${r4(start[1])}`;
        for (const [c1x, c1y, c2x, c2y, x, y] of segments) {
          d += `C${r4(f0 * c1x)} ${r4(c1y)} ${r4(f0 * c2x)} ${r4(c2y)} ${r4(f0 * x)} ${r4(y)}`;
        }
        d += `L${r4(f0 * end[0])} ${r4(hemY(f0))}L${r4(f1 * end[0])} ${r4(hemY(f1))}`;
        // And back up the second, walking its segments in reverse
        d += `L${r4(f1 * segments[segments.length - 1][4])} ${r4(segments[segments.length - 1][5])}`;
        for (let i = segments.length - 1; i >= 0; i--) {
          const [c1x, c1y, c2x, c2y] = segments[i];
          const [x, y] = i > 0 ? segments[i - 1].slice(4) : start;
          d += `C${r4(f1 * c2x)} ${r4(c2y)} ${r4(f1 * c1x)} ${r4(c1y)} ${r4(f1 * x)} ${r4(y)}`;
        }
        d += 'Z';
      }
      return d;
    },
    // The underside of every ring, from its widest edge down into the groove: in shadow, so each ring reads as a disc
    shadows(t) {
      const { segments, rings } = elanSide(t);
      let d = '';
      rings.forEach(([px, yp], i) => {
        const [c1x, c1y, c2x, c2y, xb, yb] = segments[2 * i + 1];
        d +=
          `M${r4(-px)} ${r4(yp)}Q0 ${r4(yp + 0.24 * px)} ${r4(px)} ${r4(yp)}` +
          `C${r4(c1x)} ${r4(c1y)} ${r4(c2x)} ${r4(c2y)} ${r4(xb)} ${r4(yb)}` +
          `Q0 ${r4(yb + 0.24 * xb)} ${r4(-xb)} ${r4(yb)}` +
          `C${r4(-c2x)} ${r4(c2y)} ${r4(-c1x)} ${r4(c1y)} ${r4(-px)} ${r4(yp)}Z`;
      });
      return d;
    },
    // The rings need no light line on their edges: the shadow under each one shows it
    rim() {
      return '';
    },
  },
};

/**
 * Where fin k of a Forma tier sits across the tree, from -1 (left edge) to 1 (right edge). The fins are spaced evenly
 * round the tree, so seen from the front they crowd together towards the sides. A half step is the fold between two.
 *
 * @param {number} k
 * @param {number} count - Fins across the front of a tier
 */
function formaAt(k, count) {
  return -Math.cos((Math.PI * k) / count);
}

/** How far the front of a Forma hem dips below its ends, seen from a little above. */
function formaSag(t) {
  return 0.03 * (t.hb / 0.3);
}

/**
 * The fringe of a Forma tier from left to right: the point of every fin, with a notch at the fold between each pair.
 *
 * @param {{ top: number, bot: number, hb: number, depth?: number }} t
 * @param {number} count - Fins across the front of a tier
 * @returns {number[][]}
 */
function formaHem(t, count) {
  const sag = formaSag(t);
  const depth = (t.depth ?? 0.26) * (t.bot - t.top);
  const at = (f, lift) => [f * t.hb, t.bot + sag * (1 - f * f) - lift];
  const points = [at(-1, 0)];
  for (let k = 1; k <= count; k++) {
    const fold = formaAt(k - 0.5, count);
    // The notches are deepest at the front and close up towards the sides, where the fins turn edge-on and the
    // tier below no longer fills them
    points.push(at(fold, depth * (1 - fold * fold)), at(formaAt(k, count), 0));
  }
  return points;
}

/** How far the front of an Élan hem dips below its ends. */
function elanSag(halfWidth) {
  return 0.12 * halfWidth;
}

/**
 * The right side of an Élan part, top to bottom, as cubic segments. Each ring is like a small umbrella: its top slopes
 * out almost straight to an edge low in the ring, then its short underside tucks back into the groove above the next.
 * The tip is a pointed onion: it stays narrow at first, then swells.
 *
 * @param {{ start: number[], rings: number[][] }} part
 */
function elanSide(part) {
  const segments = [];
  const rings = [];
  let [xa] = part.start;
  for (const [y, yb, px, xb, at = 0.78] of part.rings) {
    const yp = y + at * (yb - y);
    const rise = yp - y;
    const tip = xa < 0.01;
    segments.push(
      tip
        ? [xa + (px - xa) * 0.12, y + rise * 0.5, px, y + rise * 0.62, px, yp]
        : [xa + (px - xa) * 0.4, y + rise * 0.4, px - (px - xa) * 0.06, yp - rise * 0.14, px, yp]
    );
    segments.push([px, yp + (yb - yp) * 0.45, xb + (px - xb) * 0.3, yb, xb, yb]);
    rings.push([px, yp]);
    xa = xb;
  }
  const last = part.rings[part.rings.length - 1];
  return { start: part.start, segments, end: [last[3], last[1]], rings };
}

/**
 * Path data for every part of a tree, base first so the tip is drawn on top.
 *
 * @param {string} model - 'forma' or 'elan'
 * @param {number} pleats - The number of pleats drawn on each part
 * @param {number} [heightCm] - The tree's height: a Forma's shape follows it
 */
function tierPaths(model, pleats, heightCm = 240) {
  const elan = model === 'elan';
  const shape = elan ? SHAPES.elan : SHAPES.forma;
  const parts = elan ? ELAN_PARTS : formaTiers(heightCm);
  return parts
    .slice()
    .reverse()
    .map((part, i, all) => ({
      outline: shape.outline(part, pleats),
      // Every part but the top one starts in the shadow of the part above it
      shaded: i < all.length - 1,
      facets: shape.facets(part, pleats),
      shadows: shape.shadows(part, pleats),
      pleats: shape.pleats(part, pleats),
      rim: shape.rim(part, pleats),
    }));
}
// </geometry>

const NS = 'http://www.w3.org/2000/svg';

/** The scene is drawn in centimetres: one SVG unit is 1cm. */
const FLOOR = 318;
const TABLE_TOP = 243; // a 75cm dining table
const TREE_X = 190;
const TABLETOP_MAX = 60; // the 30, 45 and 60cm trees stand on the table
const HEADROOM = 10; // allow about 10cm above the tip
const CEILING = { min: 220, max: 300, step: 5, initial: 240 };
const CEILING_KEY = 'luve:live-tree:ceiling';
const CEILING_EVENT = 'luve-live-tree:ceiling';
const TONES = ['base', 'hi', 'shade', 'line'];
const MOVE_MS = 950;
const COLOUR_MS = 700;
const UNFOLD_MS = 1100;
const UNFOLD_STAGGER_MS = 85;
// Pleats never sit closer than this on screen, so a tree drawn small stays crisp instead of turning into a smudge
const MIN_PLEAT_PX = 3.5;
// Choosing a height brings the drawing into view (Ido, 21 Oct: "when he start choosing sizes it should go to the
// second tab, and if he changes sizes again that tab should stay"). The tree waits about as long as a smooth scroll or
// swipe takes to settle, so it grows where the shopper can see it; a few pixels out of view still count as in view.
const REVEAL_MS = 380;
const REVEAL_SLACK = 12;

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * The part of the scene that has to stay in view: x, y, width, height. On the floor it runs from the person to the
 * doorway and from just above the ceiling (or the tip, if the tree is taller) down to the floor. On the table it is
 * the table, the glass and the tree.
 *
 * @param {number} height - The tree, in centimetres
 * @param {number} ceiling - The ceiling, in centimetres
 */
function regionFor(height, ceiling) {
  if (height <= TABLETOP_MAX) {
    const top = Math.min(TABLE_TOP - height, 204) - 10;
    return [92, top, 206, FLOOR + 8 - top];
  }
  const top = Math.min(FLOOR - ceiling, FLOOR - height) - 18;
  return [22, top, 370, FLOOR + 10 - top];
}
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeUnfold = cubicBezier(0.22, 0.61, 0.36, 1);
const metres = (cm) => `${(cm / 100).toFixed(2)} m`;

/**
 * The page's scroll container and, in viewport coordinates, the part of the screen it shows. On desktop Horizon
 * scrolls .page-wrapper rather than the window (assets/scroll-container.js).
 */
function pageView() {
  const container = getScrollContainer();
  const root = container === document.scrollingElement || container === document.documentElement;
  const port = root ? { top: 0, bottom: window.innerHeight } : container.getBoundingClientRect();
  const header = document.querySelector('header-component');
  const bar = header instanceof HTMLElement && header.getClientRects().length ? header.getBoundingClientRect() : null;
  // Where the screen starts below the header right now: the header may not have stuck yet, or may have slid away
  const top = bar ? clamp(bar.bottom, port.top, port.bottom) : port.top;
  return { container, port, top, header, bar };
}

/**
 * @param {DOMRect} box - In viewport coordinates
 * @returns {number} How much of the box's height is on screen, below the header: 0 to 1
 */
function shareOnScreen(box) {
  const { port, top } = pageView();
  if (!box.height) return 0;
  return clamp((Math.min(box.bottom, port.bottom) - Math.max(box.top, top)) / box.height, 0, 1);
}

/**
 * How far the page has to scroll, in pixels, to show all of a box clear of the sticky header: the least distance, or
 * to the middle of the screen. 0 when the box is in full view already.
 *
 * @param {DOMRect} box - In viewport coordinates
 * @param {boolean} middle
 * @returns {number}
 */
function scrollToShow(box, middle) {
  const { container, port, top: covered, header, bar } = pageView();
  if (box.top >= covered - REVEAL_SLACK && box.bottom <= port.bottom + REVEAL_SLACK) return 0;

  // Horizon's header stays put ("always") or comes back only on the way up ("scroll-up")
  const up = middle ? box.top + box.bottom < covered + port.bottom : box.top < covered;
  const sticky = header?.getAttribute('sticky');
  const top = port.top + (bar && (sticky === 'always' || (sticky === 'scroll-up' && up)) ? bar.height : 0);

  let distance;
  if (middle) distance = (box.top + box.bottom) / 2 - (top + port.bottom) / 2;
  else if (up || box.height > port.bottom - top) distance = box.top - top;
  else distance = box.bottom - port.bottom;

  const from = container.scrollTop;
  const max = Math.max(0, container.scrollHeight - container.clientHeight);
  distance = clamp(from + distance, 0, max) - from;
  return Math.abs(distance) < 2 ? 0 : Math.round(distance);
}

let instances = 0;

/**
 * A cubic-bezier timing function, the same curve CSS and the Web Animations API would use.
 *
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @returns {(x: number) => number}
 */
function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t) => (3 * ax * t + 2 * bx) * t + cx;

  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-6) return sampleY(t);
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }

    let low = 0;
    let high = 1;
    t = x;
    while (high - low > 1e-6) {
      if (sampleX(t) < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return sampleY(t);
  };
}

/**
 * Parses a CSS colour into an [r, g, b] triple. The palette arrives from Liquid as hex.
 *
 * @param {string} colour
 * @returns {number[]}
 */
function toRgb(colour) {
  let value = String(colour).trim();

  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
    const context = document.createElement('canvas').getContext('2d');
    if (context) {
      context.fillStyle = '#000000';
      context.fillStyle = value;
      value = String(context.fillStyle);
    }
  }

  if (value.startsWith('#')) {
    const hex = value.length === 4 ? [...value.slice(1)].map((c) => c + c).join('') : value.slice(1);
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  }

  const channels = value.match(/[\d.]+/g)?.slice(0, 3).map(Number);
  return channels?.length === 3 ? channels : [110, 140, 125];
}

/**
 * @param {string | undefined} json
 * @returns {any}
 */
function parse(json) {
  try {
    return json ? JSON.parse(json) : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} tag
 * @param {Record<string, string | number>} attributes
 * @param {Element} [parent]
 */
function svg(tag, attributes, parent) {
  const element = document.createElementNS(NS, tag);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, String(value));
  parent?.append(element);
  return element;
}

/**
 * @typedef {object} TreeState
 * @property {string} model - 'forma' or 'elan'
 * @property {string[]} colour - The four tones of the paper: base, highlight, shade and pleat line
 * @property {string} label - The colour as the store names it
 * @property {number} height - In centimetres
 * @property {number} ceiling - The visitor's ceiling, in centimetres
 *
 * @typedef {object} Frame
 * @property {number} height
 * @property {number} base - The y the tree stands on
 * @property {number[]} region - The part of the scene the camera frames: x, y, width, height
 * @property {number[][]} colour - The four tones as [r, g, b]
 * @property {number} table - How visible the dining table is, 0 to 1
 */

class LuveLiveTree extends HTMLElement {
  static get observedAttributes() {
    return ['data-state', 'data-config'];
  }

  #ready = false;
  #reduced = false;
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #slideObserver = null;
  /** @type {Record<string, any>} */
  #els = {};
  /** @type {{ tier: string, shade: string, facet: string }} */
  #fills = { tier: '', shade: '', facet: '' };
  /** @type {Record<string, any>} */
  #config = {};
  /** @type {number[]} */
  #heights = [];
  /** @type {TreeState} */
  #state = { model: 'forma', colour: [], label: '', height: 150, ceiling: CEILING.initial };
  /** Whether the shopper has set their own ceiling (here, in another drawing, or on an earlier visit) */
  #ceilingSet = false;
  /** @type {Frame} */
  #frame = { height: 150, base: FLOOR, region: regionFor(150, CEILING.initial), colour: [], table: 0 };
  /** @type {{ from: Frame, to: Frame, start: number } | null} */
  #tween = null;
  /** @type {number | null} */
  #unfoldAt = null;
  #raf = 0;
  #captionTimer = 0;
  #box = { width: 0, height: 0, top: 0, bottom: 0 };
  /** @type {SVGGElement | null} */
  #tree = null;
  /** A height was just chosen: the gallery Horizon renders next opens on the drawing */
  #keepSlide = false;
  /** Carried into a gallery that opens on the drawing: show its slide as soon as it is on the page */
  #settle = false;

  connectedCallback() {
    if (!this.#ready && !this.#init()) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Capture on the document so this runs before Horizon's own listeners, which sit on the section in the bubble
    // phase. That order lets the element step into the incoming gallery before MediaGallery swaps it in.
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { capture: true, signal });
    document.addEventListener(CEILING_EVENT, this.#onCeiling, { signal });

    // The bars are watched too: when their text wraps differently the camera reframes around them
    for (const element of [this.#els.stage, this.#els.top, this.#els.bottom]) {
      if (element) this.#resizeObserver?.observe(element);
    }

    // Carried into a new gallery that opens on the drawing (#carryAcross): show it before the first paint, so the
    // photo never flashes up in between
    if (this.#settle && this.ownerDocument === document) {
      this.#settle = false;
      this.#showOwnSlide();
    }

    this.closest('slideshow-component')?.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect, {
      signal,
    });

    // When the slideshow moves on, Horizon marks this slide aria-hidden. Take its buttons out of the tab order too.
    const slide = this.closest('slideshow-slide');
    if (slide) {
      const sync = () => {
        const away = slide.getAttribute('aria-hidden') === 'true';
        for (const bar of [this.#els.top, this.#els.bottom]) if (bar) bar.inert = away;
      };
      this.#slideObserver = new MutationObserver(sync);
      this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      sync();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
  }

  /**
   * The server is the source of truth: Horizon morphs or replaces this markup, and the new attributes win.
   *
   * @param {string} name
   * @param {string | null} previous
   * @param {string | null} next
   */
  attributeChangedCallback(name, previous, next) {
    if (!this.#ready || previous === next) return;

    if (name === 'data-config') {
      this.#readConfig();
      this.#describe();
      return;
    }

    const state = this.#readState();
    if (state) this.#go(state);
  }

  /** @returns {boolean} Whether the markup was complete enough to bring to life */
  #init() {
    const find = (selector) => this.querySelector(selector);
    const scene = find('.luve-live-tree__scene');
    const stage = find('.luve-live-tree__stage');
    if (!scene || !stage) return false;

    this.#els = {
      stage,
      scene,
      trees: find('[data-lt="trees"]'),
      shadow: find('[data-lt="shadow"]'),
      table: find('[data-lt="table"]'),
      ceilingLine: find('[data-lt="ceiling-line"]'),
      ceilingLabel: find('[data-lt="ceiling-label"]'),
      dimLine: find('[data-lt="dim-line"]'),
      dimTop: find('[data-lt="dim-top"]'),
      dimBottom: find('[data-lt="dim-bottom"]'),
      dimLabel: find('[data-lt="dim-label"]'),
      labels: Array.from(this.querySelectorAll('[data-lt-label]')),
      references: Array.from(this.querySelectorAll('[data-lt-ref]')),
      top: find('.luve-live-tree__top'),
      bottom: find('.luve-live-tree__bottom'),
      ceilingValue: find('[data-lt="ceiling-value"]'),
      lower: find('[data-lt="lower"]'),
      raise: find('[data-lt="raise"]'),
      fit: find('[data-lt="fit"]'),
      caption: find('[data-lt="caption"]'),
      unfold: find('[data-lt="unfold"]'),
    };

    this.#readConfig();
    const state = this.#readState();
    if (!state || !this.#els.trees) return false;

    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#uniquifyIds();
    this.#tree = scene.querySelector('.luve-live-tree__tree');

    let ceiling = CEILING.initial;
    try {
      const saved = Number(window.localStorage.getItem(CEILING_KEY));
      if (saved) {
        ceiling = clamp(saved, CEILING.min, CEILING.max);
        this.#ceilingSet = true;
      }
    } catch {
      // Storage can be blocked. The default ceiling is fine.
    }

    this.#state = { ...state, ceiling };
    this.#frame = this.#target();
    if (!this.#tree) this.#buildTree();

    this.#resizeObserver = new ResizeObserver(this.#measure);

    const { lower, raise, unfold, top, bottom } = this.#els;
    lower?.addEventListener('click', () => this.#setCeiling(this.#state.ceiling - CEILING.step, true));
    raise?.addEventListener('click', () => this.#setCeiling(this.#state.ceiling + CEILING.step, true));
    unfold?.addEventListener('click', () => this.#unfold(0));

    // Horizon's slideshow starts a drag on mousedown anywhere in a slide. Keep it away from these controls.
    for (const bar of [top, bottom]) bar?.addEventListener('mousedown', (event) => event.stopPropagation());

    this.#ready = true;
    this.#describe();
    this.#render();
    return true;
  }

  #readConfig() {
    this.#config = parse(this.dataset.config) ?? {};
    const sizes = this.#config.sizes && typeof this.#config.sizes === 'object' ? this.#config.sizes : {};
    this.#heights = [...new Set(Object.values(sizes).map(Number))].filter((h) => h > 0).sort((a, b) => a - b);
  }

  /** @returns {Omit<TreeState, 'ceiling'> | null} */
  #readState() {
    const state = parse(this.dataset.state);
    const height = Number(state?.height);
    if (!state || !(height > 0) || !Array.isArray(state.colour) || state.colour.length !== TONES.length) return null;

    return {
      model: state.model === 'elan' ? 'elan' : 'forma',
      colour: state.colour.map(String),
      label: String(state.label ?? ''),
      height,
    };
  }

  /**
   * Gradient ids must be unique in the document. The same markup can appear twice (the slideshow and the grid) and
   * Horizon's quick add clones a product page into a dialog, so every instance renames its own.
   */
  #uniquifyIds() {
    const { scene } = this.#els;
    const suffix = `-${++instances}`;

    for (const node of scene.querySelectorAll('[id]')) {
      const id = node.id;
      const unique = `${id}${suffix}`;
      for (const user of scene.querySelectorAll(`[fill="url(#${id})"]`)) user.setAttribute('fill', `url(#${unique})`);
      node.id = unique;
    }

    this.#fills = {
      tier: `url(#${scene.querySelector('[data-lt-gradient="tier"]')?.id})`,
      shade: `url(#${scene.querySelector('[data-lt-gradient="shade"]')?.id})`,
      facet: `url(#${scene.querySelector('[data-lt-gradient="facet"]')?.id})`,
    };
  }

  /**
   * @param {Event & { selectedOptions?: { name: string, value: string }[], product?: { id?: string }, promise?: Promise<any> }} event
   */
  #onProductSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || target.closest('product-card')) return;

    const scope = this.closest('.shopify-section, dialog');
    if (!scope || target.closest('.shopify-section, dialog') !== scope) return;

    const productId = event.product?.id ? String(event.product.id).split('/').pop() : '';
    if (productId && productId !== this.dataset.productId) return;

    // A new height brings the drawing into view, and the tree grows once it is there
    const next = this.#fromOptions(event.selectedOptions);
    const resized = next.height !== undefined && next.height !== this.#state.height;
    const delay = resized ? this.reveal() : 0;

    // Move straight away: the request for the new section is still in flight
    this.#go(next, { delay });

    event.promise
      ?.then((result) => this.#carryAcross(result?.detail))
      // A selection that is overtaken by the next one is aborted. The next one brings the drawing up to date.
      .catch(() => {});
  };

  /**
   * Horizon's MediaGallery replaces itself with the gallery from the section response. Before it does, put this live
   * element in place of its fresh copy, so the scene keeps animating instead of restarting from a still frame.
   *
   * @param {{ html?: Document } | undefined} detail
   */
  #carryAcross(detail) {
    const html = detail?.html;
    if (!html?.querySelector || !this.isConnected || !this.closest('media-gallery')) return;

    const gallery = html.querySelector('media-gallery');
    const incoming = gallery?.querySelector(`luve-live-tree[data-slot="${this.dataset.slot}"]`);
    if (!incoming || incoming.getAttribute('data-product-id') !== this.dataset.productId) return;

    // The response is the source of truth for the variant that ended up selected
    for (const name of LuveLiveTree.observedAttributes) {
      const value = incoming.getAttribute(name);
      if (value !== null && value !== this.getAttribute(name)) this.setAttribute(name, value);
    }

    // The new gallery opens on the photo. Keep it on the drawing when the shopper is looking at it or has just chosen
    // a height (Ido, 21 Oct: "if he changes sizes again that tab should stay")
    if (this.dataset.slot === 'slideshow') {
      const slideshow = /** @type {any} */ (this.closest('slideshow-component'));
      const onIt = slideshow?.slides?.[slideshow.current] === this.closest('slideshow-slide');
      const stay = this.#keepSlide || onIt;
      this.#keepSlide = false;

      const show = incoming.closest('slideshow-component');
      const slides = show
        ? Array.from(show.querySelectorAll('slideshow-slide')).filter((s) => s.closest('slideshow-component') === show)
        : [];
      const index = slides.indexOf(/** @type {Element} */ (incoming.closest('slideshow-slide')));
      if (stay && show && index > 0) {
        // Horizon opens on initial-slide only when that slide has a slide-id: without one its setup goes back to the
        // first slide a frame later. The gallery's slides carry none, so the drawing's gets its own.
        if (!slides[index].hasAttribute('slide-id')) slides[index].setAttribute('slide-id', 'luve-live-tree');
        show.setAttribute('initial-slide', String(index));
        this.#settle = true;
      }
    }

    const parent = this.parentNode;
    const next = this.nextSibling;
    incoming.replaceWith(this);

    // Safety net: if nothing puts the new gallery on the page, return to where this element was
    setTimeout(() => {
      if (this.isConnected || !parent?.isConnected) return;
      parent.insertBefore(this, next?.parentNode === parent ? next : null);
    });
  }

  /** @param {{ name: string, value: string }[] | undefined} selected */
  #fromOptions(selected) {
    const { colourOption, sizeOption, colours = {}, sizes = {} } = this.#config;
    /** @type {Partial<TreeState>} */
    const next = {};

    for (const option of Array.isArray(selected) ? selected : []) {
      if (option?.name === colourOption && Array.isArray(colours[option.value])) {
        next.colour = colours[option.value].map(String);
        next.label = option.value;
      } else if (option?.name === sizeOption && Number(sizes[option.value]) > 0) {
        next.height = Number(sizes[option.value]);
      }
    }

    return next;
  }

  /**
   * Brings the drawing into view. On a phone it is a slide of the gallery: the gallery moves to it (with a swipe when
   * the gallery is on screen) and stays on it through the re-render that follows a selection. On desktop it is a cell
   * of the grid. Either way the page scrolls only when the drawing is not in full view already: on a phone the least
   * distance, so the choices below stay as close as they can; on desktop to the middle of the screen, while the sticky
   * details keep the choices where they are. Used when a height is chosen, and by "See it to scale"
   * (blocks/luve-scale-link.liquid).
   *
   * @returns {number} How long the drawing takes to arrive, in milliseconds: 0 when nothing had to move
   */
  reveal() {
    const { slot } = this.dataset;
    // The copy the page hides (the grid on a phone, the slideshow on desktop) and the thumbnail never act
    if (!this.#ready || (slot !== 'slideshow' && slot !== 'grid') || !this.getClientRects().length) return 0;

    const smooth = !this.#reduced;
    const slideshow = /** @type {any} */ (slot === 'slideshow' ? this.closest('slideshow-component') : null);
    const frame = slideshow ?? this.closest('.luve-live-tree-cell') ?? this;
    let wait = 0;

    if (slideshow) {
      const slide = this.closest('slideshow-slide');
      const slides = Array.isArray(slideshow.slides) ? slideshow.slides : [];
      const index = slides.indexOf(slide);
      if (index >= 0 && slides[slideshow.current] !== slide && typeof slideshow.select === 'function') {
        const animate = smooth && shareOnScreen(frame.getBoundingClientRect()) >= 0.5;
        slideshow.select(index, undefined, { animate });
        if (animate) wait = REVEAL_MS;
      }
      this.#keepSlide = true;
    }

    // Quick add shows the product in a dialog, which keeps its own scroll
    if (!this.closest('dialog')) {
      const distance = scrollToShow(frame.getBoundingClientRect(), !slideshow);
      if (distance) {
        getScrollContainer().scrollBy({ top: distance, behavior: smooth ? 'smooth' : 'instant' });
        if (smooth) wait = REVEAL_MS;
      }
    }

    return wait;
  }

  /**
   * Puts this drawing's slide in view in its slideshow at once. Horizon's slideshow takes initial-slide for its count
   * and dots, but its first select() returns early because that slide already counts as current, and it would run two
   * frames late, flashing the photo. Its own observer then keeps aria-hidden; until it does, the drawing is the slide
   * assistive tech reads, and its buttons are not inert. (#carryAcross also gives the slide a slide-id, without which
   * Horizon's setup would go back to the first slide.)
   */
  #showOwnSlide() {
    const slide = this.closest('slideshow-slide');
    const scroller = slide?.parentElement;
    if (!slide || !(scroller instanceof HTMLElement)) return;

    // Slides snap to the centre of the scroller
    const box = slide.getBoundingClientRect();
    const port = scroller.getBoundingClientRect();
    const offset = box.left + box.width / 2 - (port.left + port.width / 2);
    if (Math.abs(offset) >= 1) scroller.scrollTo({ left: scroller.scrollLeft + offset, behavior: 'instant' });

    for (const other of scroller.children) {
      if (other.localName === 'slideshow-slide') other.setAttribute('aria-hidden', String(other !== slide));
    }
  }

  /**
   * The shopper swiped, or tapped a dot, to another slide: the next gallery opens where Horizon puts it.
   *
   * @param {Event & { detail?: { userInitiated?: boolean, slide?: Element } }} event
   */
  #onSlideshowSelect = (event) => {
    if (event.target !== this.closest('slideshow-component')) return;
    if (event.detail?.userInitiated && event.detail.slide !== this.closest('slideshow-slide')) this.#keepSlide = false;
  };

  /**
   * @param {Partial<TreeState>} next
   * @param {{ animate?: boolean, delay?: number }} [options] - delay: how long the tree holds still, in milliseconds,
   *   while the drawing comes into view
   */
  #go(next, { animate = true, delay = 0 } = {}) {
    const previous = this.#state;
    const state = { ...previous, ...next };
    this.#state = state;

    const modelChanged = state.model !== previous.model;
    const moved =
      modelChanged || state.height !== previous.height || state.colour.join() !== previous.colour.join();

    if (modelChanged) {
      this.#buildTree();
      this.#unfold(120);
    } else if (moved) {
      this.#syncPleats();
    }

    if (moved) this.#moveTo(animate, delay);
    this.#describe();
  }

  /** @returns {Frame} Where the scene comes to rest for the current state */
  #target() {
    const { height, colour, ceiling } = this.#state;
    const table = height <= TABLETOP_MAX;
    return {
      height,
      base: table ? TABLE_TOP : FLOOR,
      region: regionFor(height, ceiling),
      colour: colour.map(toRgb),
      table: table ? 1 : 0,
    };
  }

  /**
   * @param {boolean} animate
   * @param {number} [delay] - In milliseconds: until then the tween holds its first frame
   */
  #moveTo(animate, delay = 0) {
    const to = this.#target();

    if (!animate || this.#reduced) {
      this.#tween = null;
      this.#frame = to;
      this.#render();
      return;
    }

    this.#tween = { from: this.#frame, to, start: performance.now() + delay };
    this.#tick();
  }

  #tick() {
    if (!this.#raf) this.#raf = requestAnimationFrame(this.#step);
  }

  /** @param {number} now */
  #step = (now) => {
    this.#raf = 0;
    let running = false;

    const tween = this.#tween;
    if (tween) {
      const { from, to, start } = tween;
      const move = easeOut(clamp((now - start) / MOVE_MS, 0, 1));
      const tint = easeUnfold(clamp((now - start) / COLOUR_MS, 0, 1));

      this.#frame = {
        height: lerp(from.height, to.height, move),
        base: lerp(from.base, to.base, move),
        region: from.region.map((value, i) => lerp(value, to.region[i], move)),
        colour: from.colour.map((rgb, i) => rgb.map((value, j) => lerp(value, to.colour[i][j], tint))),
        table: lerp(from.table, to.table, move),
      };

      if (move < 1 || tint < 1) running = true;
      else this.#tween = null;
    }

    if (this.#applyUnfold(now)) running = true;
    this.#render();
    if (running) this.#tick();
  };

  /**
   * The unfold: every tier fans open from flat, overshoots a touch as the magnets catch, then settles. It is driven
   * from the same frame loop as everything else, so it survives the element being moved between galleries.
   *
   * @param {number} delay
   */
  #unfold(delay) {
    if (this.#reduced || !this.#tree) return;
    this.#unfoldAt = performance.now() + delay;
    this.#applyUnfold(performance.now());
    this.#tick();
    this.#showCaption(delay + 380);
  }

  /**
   * @param {number} now
   * @returns {boolean} Whether the unfold is still playing
   */
  #applyUnfold(now) {
    if (this.#unfoldAt === null) return false;

    const tiers = /** @type {SVGGElement[]} */ (Array.from(this.#tree?.children ?? []));
    const elapsed = now - this.#unfoldAt;
    const done = elapsed >= UNFOLD_MS + (tiers.length - 1) * UNFOLD_STAGGER_MS;

    tiers.forEach((tier, i) => {
      if (done) {
        tier.style.removeProperty('transform');
        tier.style.removeProperty('opacity');
        return;
      }

      const p = easeUnfold(clamp((elapsed - i * UNFOLD_STAGGER_MS) / UNFOLD_MS, 0, 1));
      const [scale, opacity] =
        p < 0.8 ? [lerp(0.04, 1.035, p / 0.8), lerp(0.55, 1, p / 0.8)] : [lerp(1.035, 1, (p - 0.8) / 0.2), 1];
      tier.style.transform = `scaleX(${r4(scale)})`;
      tier.style.opacity = String(r4(opacity));
    });

    if (done) this.#unfoldAt = null;
    return !done;
  }

  /** @param {number} delay */
  #showCaption(delay) {
    const { caption } = this.#els;
    if (!caption) return;

    clearTimeout(this.#captionTimer);
    this.#captionTimer = window.setTimeout(() => {
      caption.classList.add('is-visible');
      this.#captionTimer = window.setTimeout(() => caption.classList.remove('is-visible'), 2600);
    }, delay);
  }

  #buildTree() {
    const { model, height } = this.#state;
    const pleats = this.#pleatsFor(height);
    // Placed at once: the next animation frame can be a while away in a background tab
    const tree = svg('g', {
      class: 'luve-live-tree__tree',
      'data-model': model,
      'data-pleats': pleats,
      'data-shape': shapeKey(model, height),
      transform: `translate(${TREE_X} ${r4(this.#frame.base)}) scale(${r4(this.#frame.height)})`,
    });

    for (const tier of tierPaths(model, pleats, height)) {
      const group = svg('g', { class: 'luve-live-tree__tier' }, tree);
      svg('path', { d: tier.outline, fill: this.#fills.tier }, group);
      if (tier.shaded) svg('path', { d: tier.outline, fill: this.#fills.shade }, group);
      svg('path', { d: tier.facets, class: 'luve-live-tree__facets', fill: this.#fills.facet }, group);
      if (tier.shadows) svg('path', { d: tier.shadows, class: 'luve-live-tree__shadows' }, group);
      svg('path', { d: tier.pleats, class: 'luve-live-tree__pleats' }, group);
      if (tier.rim) svg('path', { d: tier.rim, class: 'luve-live-tree__rim' }, group);
    }

    if (this.#tree) this.#tree.replaceWith(tree);
    else this.#els.trees.append(tree);
    this.#tree = /** @type {SVGGElement} */ (tree);

    if (this.#unfoldAt !== null) this.#applyUnfold(performance.now());
  }

  /** The tree is redrawn when its model, its shape (a Forma's height) or its pleat count on screen changes. */
  #syncPleats() {
    const tree = this.#tree;
    if (!tree) return;

    const { model, height } = this.#state;
    const pleats = this.#pleatsFor(height);
    // A Forma changes shape with its height: the tabletop sizes are broad cones, the floor sizes slim spires
    if (tree.dataset.model !== model || Number(tree.dataset.pleats) !== pleats || tree.dataset.shape !== shapeKey(model, height)) {
      this.#buildTree();
    }
  }

  /** @param {number} height */
  #pleatsFor(height) {
    const [, , viewWidth] = this.#camera(regionFor(height, this.#state.ceiling));
    const pixelsPerUnit = (this.#box.width || 400) / viewWidth;
    const { model } = this.#state;
    const treeWidth = height * 2 * halfFor(model, height) * pixelsPerUnit;
    // As many as the real tree shows, unless the tree is drawn too small to keep them apart
    const fins = finsFor(model, height);
    return clamp(Math.min(fins, Math.round(treeWidth / MIN_PLEAT_PX)), Math.min(6, fins), fins);
  }

  /**
   * Frames a region of the scene inside the slide, clear of the controls at the top and bottom. The wall and the
   * floor are drawn far past the room, so whatever is left over on any side is still the room.
   *
   * @param {number[]} region
   * @returns {number[]} The viewBox: x, y, width, height
   */
  #camera(region) {
    const [x, y, width, height] = region;
    const box = this.#box;
    if (!box.width || !box.height) return region;

    const side = Math.min(16, box.width * 0.04);
    const availableWidth = Math.max(1, box.width - 2 * side);
    const availableHeight = Math.max(1, box.height - box.top - box.bottom);
    const scale = Math.min(availableWidth / width, availableHeight / height);

    return [
      x - side / scale - (availableWidth / scale - width) / 2,
      y - box.top / scale - (availableHeight / scale - height) / 2,
      box.width / scale,
      box.height / scale,
    ];
  }

  #measure = () => {
    const { stage, top, bottom } = this.#els;
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    // A slide that is scrolled away is skipped by content-visibility and measures 0
    if (!width || !height) return;

    this.style.setProperty('--lt-controls-space', `${this.#controlsSpace()}px`);

    this.#box = {
      width,
      height,
      top: top ? top.offsetTop + top.offsetHeight + 4 : 0,
      bottom: bottom ? height - bottom.offsetTop + 4 : 0,
    };

    this.#syncPleats();
    this.#render();
  };

  /** Room for Horizon's dots or counter, which sit on top of the slideshow. */
  #controlsSpace() {
    if (this.dataset.slot !== 'slideshow') return 0;

    let space = 0;
    const controls = this.closest('slideshow-component')?.querySelectorAll('slideshow-controls[controls-on-media]');
    for (const control of controls ?? []) {
      if (!control.hasAttribute('thumbnails') && control.getClientRects().length) {
        space = Math.max(space, control.getBoundingClientRect().height);
      }
    }
    return Math.round(space);
  }

  #render() {
    const { scene, shadow, table, dimLine, dimTop, dimBottom, dimLabel, ceilingLine, ceilingLabel, labels } = this.#els;
    const frame = this.#frame;
    const { model, ceiling } = this.#state;

    const [vx, vy, vw, vh] = this.#camera(frame.region);
    // SVG units per CSS pixel: labels and ticks are sized in pixels so they read the same on every screen.
    // Labels step down a size on a small slide, where the room itself is drawn small.
    const unit = vw / (this.#box.width || 400);
    const type = unit * (this.#box.width && this.#box.width < 480 ? 10 / 11 : 1);

    scene.setAttribute('viewBox', `${r4(vx)} ${r4(vy)} ${r4(vw)} ${r4(vh)}`);
    TONES.forEach((tone, i) => {
      const rgb = frame.colour[i];
      if (rgb) scene.style.setProperty(`--lt-tree-${tone}`, `rgb(${rgb.map((c) => Math.round(c)).join(', ')})`);
    });

    // The drawn shape is the one for the height being shown (a Forma's width changes with its height)
    const half = halfFor(model, this.#state.height) * frame.height;
    this.#tree?.setAttribute('transform', `translate(${TREE_X} ${r4(frame.base)}) scale(${r4(frame.height)})`);

    shadow?.setAttribute('cx', String(TREE_X));
    shadow?.setAttribute('cy', String(r4(frame.base + 0.8)));
    shadow?.setAttribute('rx', String(r4(half * 1.15)));
    shadow?.setAttribute('ry', String(r4(Math.max(1.4, frame.height * 0.022))));
    // On the table the person and the doorway step out: the glass is the reference there
    table?.setAttribute('opacity', String(r4(frame.table)));
    for (const reference of this.#els.references) reference.setAttribute('opacity', String(r4(1 - frame.table)));

    // The height dimension beside the tree: on the right in the room, on the left on the table where the glass
    // stands on the right, and flipped left whenever it would run out of the frame
    let anchor = frame.table > 0.5 ? 'end' : 'start';
    if (anchor === 'start' && TREE_X + half + 12 * unit > vx + vw - 52 * unit) anchor = 'end';
    const x = anchor === 'start' ? TREE_X + half + 12 * unit : TREE_X - half - 12 * unit;
    const top = frame.base - frame.height;
    const tick = 4 * unit;
    this.#line(dimLine, x, top, x, frame.base);
    this.#line(dimTop, x - tick, top, x + tick, top);
    this.#line(dimBottom, x - tick, frame.base, x + tick, frame.base);
    if (dimLabel) {
      const labelX = anchor === 'start' ? x + 6 * unit : x - 6 * unit;
      dimLabel.setAttribute('text-anchor', anchor);
      dimLabel.setAttribute('transform', `translate(${r4(labelX)} ${r4(top + frame.height / 2 + 4 * unit)}) scale(${r4(type)})`);
      dimLabel.textContent = `${Math.round(frame.height)} cm`;
    }

    const ceilingY = FLOOR - ceiling;
    ceilingLine?.setAttribute('y1', String(ceilingY));
    ceilingLine?.setAttribute('y2', String(ceilingY));
    // At the right of the room rather than the frame, so a wide slide does not strand it out on the wall
    const [rx, , rw] = frame.region;
    ceilingLabel?.setAttribute(
      'transform',
      `translate(${r4(Math.min(vx + vw - 12 * unit, rx + rw))} ${r4(ceilingY - 6 * unit)}) scale(${r4(type)})`
    );

    for (const label of labels) {
      label.setAttribute('transform', `translate(${label.dataset.x} ${label.dataset.y}) scale(${r4(type)})`);
    }
  }

  /**
   * @param {Element | null} line
   * @param {number} x1
   * @param {number} y1
   * @param {number} x2
   * @param {number} y2
   */
  #line(line, x1, y1, x2, y2) {
    if (!line) return;
    line.setAttribute('x1', String(r4(x1)));
    line.setAttribute('y1', String(r4(y1)));
    line.setAttribute('x2', String(r4(x2)));
    line.setAttribute('y2', String(r4(y2)));
  }

  /** The words: the fit under the ceiling, the ceiling control and the description of the drawing. */
  #describe() {
    const { fit, ceilingValue, ceilingLabel, ceilingLine, lower, raise, scene } = this.#els;
    const { height, ceiling, label } = this.#state;
    const table = height <= TABLETOP_MAX;
    const fits = table || height + HEADROOM <= ceiling;
    // Until the shopper sets their own ceiling, a tree too tall for the assumed 2.40 m says what it needs, without the
    // warning (Ido, 23 Oct 2026): most shoppers never touch the control, so the red met everyone who picked a 240cm
    const warn = !fits && this.#ceilingSet;

    let message;
    if (table) {
      message = 'Sits on a dining table, 75cm high. Shown beside a 22cm wine glass.';
    } else if (fits) {
      message = `Fits under a ${metres(ceiling)} ceiling with ${ceiling - height}cm to spare.`;
    } else if (!this.#ceilingSet) {
      message = `Needs a ceiling of about ${metres(height + HEADROOM)}. Set yours above to check.`;
    } else {
      const lowerSize = this.#heights.filter((h) => h > TABLETOP_MAX && h + HEADROOM <= ceiling).pop();
      message = `Too tall for a ${metres(ceiling)} ceiling. Allow about ${HEADROOM}cm above the tip${
        lowerSize ? `: choose ${lowerSize}cm or lower.` : '.'
      }`;
    }

    if (fit && fit.textContent !== message) fit.textContent = message;
    fit?.classList.toggle('is-warn', warn);
    ceilingLine?.classList.toggle('is-warn', warn);
    ceilingLabel?.classList.toggle('is-warn', warn);
    if (ceilingLabel) ceilingLabel.textContent = `Ceiling ${metres(ceiling)}`;
    if (ceilingValue) ceilingValue.textContent = metres(ceiling);
    if (lower) lower.disabled = ceiling <= CEILING.min;
    if (raise) raise.disabled = ceiling >= CEILING.max;

    const title = this.#config.title || 'The tree';
    scene.setAttribute(
      'aria-label',
      `${title} in ${label}, ${height}cm, ${
        table ? 'on a dining table beside a 22cm wine glass' : 'on the floor beside a 165cm person and a 2.04 m doorway'
      }, under a ${metres(ceiling)} ceiling`
    );
  }

  /**
   * @param {number} value
   * @param {boolean} share - Remember it and tell the other drawings on the page
   */
  #setCeiling(value, share) {
    if (!Number.isFinite(value)) return;
    const ceiling = clamp(Math.round(value / CEILING.step) * CEILING.step, CEILING.min, CEILING.max);
    // A press, here or in another drawing on the page, means the shopper has a ceiling in mind: warnings apply now
    const firstSet = !this.#ceilingSet;
    this.#ceilingSet = true;
    if (ceiling === this.#state.ceiling) {
      if (firstSet) this.#describe();
      return;
    }

    this.#state = { ...this.#state, ceiling };
    this.#describe();
    // The ceiling line moves at once, the camera eases to take in the new ceiling
    this.#moveTo(true);
    if (!share) return;

    try {
      window.localStorage.setItem(CEILING_KEY, String(ceiling));
    } catch {
      // Storage can be blocked. The ceiling still applies on this page.
    }
    document.dispatchEvent(new CustomEvent(CEILING_EVENT, { detail: { ceiling, source: this } }));
  }

  /** @param {Event & { detail?: { ceiling?: number, source?: Element } }} event */
  #onCeiling = (event) => {
    if (event.detail?.source !== this) this.#setCeiling(Number(event.detail?.ceiling), false);
  };
}

if (!customElements.get('luve-live-tree')) {
  customElements.define('luve-live-tree', LuveLiveTree);
}

// The geometry, for the set builder (assets/luve-trio-builder.js). The import map points @theme/luve-live-tree at this
// same file, so it is the one module instance and the very same shapes.
export { tierPaths, halfFor, finsFor };
